import { useRef, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, CheckCircle, FileCheck, Search, Handshake, BarChart3, ExternalLink, Linkedin, Instagram, Twitter, Youtube } from "lucide-react";

const CERT_URLS = {
  ksum: "https://customer-assets.emergentagent.com/job_bridge-build-1/artifacts/h3cl7853_Kerala%20startup%20mission.pdf",
  msme: "https://customer-assets.emergentagent.com/job_bridge-build-1/artifacts/mm2trsn7_msme%20udayam.pdf",
  startup_india: "https://customer-assets.emergentagent.com/job_bridge-build-1/artifacts/70e34e23_Startup%20India%20Certificate.pdf",
};

/* ─── Connecting Nodes Canvas ─────────────────────────────── */
function ConnectingNodes() {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const nodesRef = useRef([]);

  const initNodes = useCallback((w, h) => {
    const count = Math.floor((w * h) / 18000);
    return Array.from({ length: Math.min(count, 60) }, () => ({
      x: Math.random() * w, y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4,
      r: Math.random() * 2 + 1,
      color: Math.random() > 0.5 ? "rgba(220,38,38,0.35)" : "rgba(15,23,42,0.2)",
    }));
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const resize = () => {
      canvas.width = canvas.parentElement.offsetWidth;
      canvas.height = canvas.parentElement.offsetHeight;
      nodesRef.current = initNodes(canvas.width, canvas.height);
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      const { width: w, height: h } = canvas;
      ctx.clearRect(0, 0, w, h);
      const nodes = nodesRef.current;
      for (const n of nodes) {
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
      }
      // Draw connections
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 150) {
            const alpha = (1 - dist / 150) * 0.15;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = i % 3 === 0 ? `rgba(220,38,38,${alpha})` : `rgba(15,23,42,${alpha})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      // Draw nodes
      for (const n of nodes) {
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = n.color;
        ctx.fill();
      }
      animRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      window.removeEventListener("resize", resize);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [initNodes]);

  return <canvas ref={canvasRef} />;
}

/* ─── Shared Footer ──────────────────────────────────────── */
export function SiteFooter() {
  return (
    <footer className="bg-white border-t border-slate-200" data-testid="site-footer">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Left Column: Logo + Trust Badges */}
          <div className="md:col-span-4">
            <img src="/logo-full.jpg" alt="ImpactX Bridge" className="h-14 object-contain mb-5" data-testid="footer-logo" />
            <div className="flex flex-wrap gap-2 mt-3">
              <span className="inline-flex items-center gap-1.5 text-[10px] px-2.5 py-1 rounded border border-slate-200 text-slate-500 bg-slate-50 font-medium">
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                SSL Secured
              </span>
              <span className="inline-flex items-center gap-1.5 text-[10px] px-2.5 py-1 rounded border border-slate-200 text-slate-500 bg-slate-50 font-medium">
                80G Verified
              </span>
              <span className="inline-flex items-center gap-1.5 text-[10px] px-2.5 py-1 rounded border border-slate-200 text-slate-500 bg-slate-50 font-medium">
                12A Compliant
              </span>
            </div>
          </div>

          {/* Middle: Company + Legal */}
          <div className="md:col-span-2">
            <p className="font-semibold text-ix-black text-sm mb-3">Company</p>
            <div className="space-y-2">
              <Link to="/about-us" className="block text-sm text-slate-500 hover:text-ix-red-600 transition-colors">About Us</Link>
              <Link to="/contact-us" className="block text-sm text-slate-500 hover:text-ix-red-600 transition-colors">Contact</Link>
              <Link to="/faq" className="block text-sm text-slate-500 hover:text-ix-red-600 transition-colors">FAQ</Link>
            </div>
          </div>
          <div className="md:col-span-2">
            <p className="font-semibold text-ix-black text-sm mb-3">Legal</p>
            <div className="space-y-2">
              <Link to="/privacy-policy" className="block text-sm text-slate-500 hover:text-ix-red-600 transition-colors">Privacy Policy</Link>
              <Link to="/terms-and-conditions" className="block text-sm text-slate-500 hover:text-ix-red-600 transition-colors">Terms & Conditions</Link>
            </div>
          </div>

          {/* Right Column: Registered Office */}
          <div className="md:col-span-4">
            <p className="font-semibold text-ix-black text-sm mb-3">Registered Office</p>
            <p className="text-sm text-slate-500 leading-relaxed">
              ImpactX Bridge Pvt Ltd<br />
              Kerala, India
            </p>
            <a href="mailto:connect@impactxbridge.com" className="text-sm text-ix-red-600 hover:underline mt-2 inline-block">connect@impactxbridge.com</a>

            {/* Social Icons */}
            <div className="flex items-center gap-2 mt-4">
              <a href="https://www.linkedin.com/company/impactxbridge" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-md bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 hover:text-blue-700 hover:bg-blue-50 hover:border-blue-200 transition-colors" data-testid="footer-linkedin">
                <Linkedin className="w-3.5 h-3.5" />
              </a>
              <a href="https://www.instagram.com/impactxbridge" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-md bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 hover:text-pink-600 hover:bg-pink-50 hover:border-pink-200 transition-colors" data-testid="footer-instagram">
                <Instagram className="w-3.5 h-3.5" />
              </a>
              <a href="https://x.com/Impactxbridge" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-md bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 hover:text-ix-black hover:bg-slate-100 hover:border-slate-300 transition-colors" data-testid="footer-x">
                <Twitter className="w-3.5 h-3.5" />
              </a>
              <a href="https://youtube.com/@impactxbridge" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-md bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 hover:border-red-200 transition-colors" data-testid="footer-youtube">
                <Youtube className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-100 mt-10 pt-6">
          <p className="text-center text-xs text-slate-400">&copy; {new Date().getFullYear()} ImpactX Bridge Pvt Ltd. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}

/* ─── Shared Nav ─────────────────────────────────────────── */
export function SiteNav({ minimal }) {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const goToDashboard = () => {
    if (user?.role === "ngo") navigate("/ngo-dashboard");
    else if (user?.role === "corporate") navigate("/corporate-dashboard");
    else if (user?.role === "admin") navigate("/admin-portal");
    else navigate("/auth");
  };
  return (
    <nav className="bg-white border-b border-slate-100 fixed top-0 left-0 right-0 z-50 h-16" data-testid="site-nav">
      <div className="max-w-7xl mx-auto h-full flex items-center justify-between px-6">
        <Link to="/" className="flex items-center gap-2.5">
          <img src="/logo-icon.png" alt="X" className="h-8 w-8 object-contain" />
          <span className="font-bold text-lg text-ix-black tracking-tight">Impact<span className="text-ix-red-600">X</span> Bridge</span>
        </Link>
        {!minimal && (
          <div className="hidden md:flex items-center gap-6 text-sm">
            <Link to="/" className="text-slate-600 hover:text-ix-black transition-colors" data-testid="nav-home">Home</Link>
            <Link to="/about-us" className="text-slate-600 hover:text-ix-black transition-colors" data-testid="nav-about">About</Link>
            <Link to="/faq" className="text-slate-600 hover:text-ix-black transition-colors" data-testid="nav-faq">FAQ</Link>
          </div>
        )}
        <div className="flex items-center gap-3">
          {!loading && user ? (
            <Button className="bg-ix-red-600 text-white hover:bg-ix-red-700 rounded-md px-5 shadow-sm" onClick={goToDashboard} data-testid="go-to-dashboard-btn">Dashboard <ArrowRight className="w-4 h-4 ml-1" /></Button>
          ) : (
            <>
              <Button variant="ghost" className="text-slate-600 hover:text-ix-black" onClick={() => navigate("/auth")} data-testid="login-btn">Log In</Button>
              <Button className="bg-ix-red-600 text-white hover:bg-ix-red-700 rounded-md px-5 shadow-sm" onClick={() => navigate("/auth")} data-testid="get-started-btn">Get Started</Button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

/* ─── Landing Page ───────────────────────────────────────── */
export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="landing-page">
      <SiteNav />

      {/* Hero */}
      <section className="relative pt-16 bg-white overflow-hidden" data-testid="hero-section">
        <div className="hero-canvas-container"><ConnectingNodes /></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 lg:py-32">
          <div className="max-w-3xl">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-ix-red-600 mb-4 animate-fade-in">India's CSR-Tech Infrastructure</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-ix-black tracking-tight leading-[1.1] animate-fade-in stagger-1">
              The Secure Bridge Between <span className="text-ix-red-600">CSR Funds</span> & Verified NGOs
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl animate-fade-in stagger-2">
              A unified platform for Corporate CSR teams and grassroots NGOs to discover, fund, and scale high-trust partnerships.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 animate-fade-in stagger-3">
              <Button className="bg-ix-red-600 text-white hover:bg-ix-red-700 rounded-md px-8 py-3 h-auto text-base shadow-sm" onClick={() => navigate("/auth")} data-testid="hero-cta-ngo">
                Join as an NGO <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="outline" className="border-slate-300 text-ix-black hover:bg-slate-50 rounded-md px-8 py-3 h-auto text-base" onClick={() => navigate("/auth")} data-testid="hero-cta-corporate">
                Partner as a CSR Team
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Marquee */}
      <section className="bg-white border-y border-slate-100 py-6" data-testid="trust-marquee">
        <p className="text-center text-xs tracking-[0.15em] uppercase font-semibold text-slate-400 mb-4">Officially Recognised By</p>
        <div className="marquee-wrapper">
          <div className="marquee-track">
            {[0, 1].map((dup) => (
              <div key={dup} className="flex items-center gap-16 px-8">
                <a href={CERT_URLS.startup_india} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity cursor-pointer" data-testid="marquee-startup-india">
                  <img src="/startup-india.png" alt="Startup India" className="h-10 object-contain" />
                </a>
                <a href={CERT_URLS.startup_india} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity cursor-pointer" data-testid="marquee-dpiit">
                  <img src="/dpiit.jpg" alt="DPIIT" className="h-10 object-contain" />
                </a>
                <a href={CERT_URLS.ksum} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity cursor-pointer" data-testid="marquee-ksum">
                  <img src="/ksum.svg" alt="Kerala Startup Mission" className="h-10 object-contain" />
                </a>
                <a href={CERT_URLS.msme} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity cursor-pointer" data-testid="marquee-msme">
                  <img src="/msme.svg" alt="MSME" className="h-10 object-contain" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6 bg-slate-50" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-ix-red-600 mb-3">Process</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-ix-black tracking-tight mb-4">How It Works</h2>
          <p className="text-base text-slate-500 mb-16 max-w-xl">From onboarding to impact in four streamlined steps.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: "01", icon: FileCheck, title: "Onboard & Verify", desc: "NGOs and CSR teams complete onboarding. We verify profiles in 3-5 days." },
              { step: "02", icon: Search, title: "Discover & Match", desc: "CSR teams discover vetted NGOs through smart filters and intelligent matching." },
              { step: "03", icon: Handshake, title: "Fund & Track", desc: "Transparent, milestone-based fund disbursals with real-time tracking." },
              { step: "04", icon: BarChart3, title: "Report & Scale", desc: "Structured impact reports and audit-friendly ESG data." },
            ].map((item, i) => (
              <Card key={i} className="bg-white border border-slate-200 rounded-lg shadow-sm hover:-translate-y-[1px] hover:shadow-md transition-all duration-200">
                <CardContent className="p-6">
                  <span className="text-xs font-bold text-ix-red-600 tracking-wider">{item.step}</span>
                  <div className="w-10 h-10 bg-red-50 rounded-md flex items-center justify-center mt-4 mb-4">
                    <item.icon className="w-5 h-5 text-ix-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-ix-black mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* For NGOs / For Corporates */}
      <section className="py-24 px-6 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-ix-red-600 mb-3">For NGOs</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-ix-black tracking-tight mb-6">Get Discovered by CSR Funders</h2>
            <ul className="space-y-4">
              {["Quick onboarding & verification", "Match by mission, geography, and strengths", "Clear funding progress & milestone updates", "Built-in compliance guidance (80G, 12A, CSR-1)", "Structured reporting support"].map((t, i) => (
                <li key={i} className="flex items-start gap-3"><CheckCircle className="w-4 h-4 text-ix-red-600 mt-0.5 flex-shrink-0" /><span className="text-sm text-slate-600">{t}</span></li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-ix-red-600 mb-3">For Corporates</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-ix-black tracking-tight mb-6">Streamline Your CSR Initiatives</h2>
            <ul className="space-y-4">
              {["100% verified, impact-ready NGOs", "Transparent fund disbursal workflows", "Audit-friendly ESG reports", "Filter by cause, region, compliance fit", "Real-time project status & tracking"].map((t, i) => (
                <li key={i} className="flex items-start gap-3"><CheckCircle className="w-4 h-4 text-ix-red-600 mt-0.5 flex-shrink-0" /><span className="text-sm text-slate-600">{t}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Accreditations & Certificates Gallery */}
      <section className="py-24 px-6 bg-slate-50" data-testid="certificates-section">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-ix-red-600 mb-3 text-center">Trust & Compliance</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-ix-black tracking-tight mb-4 text-center">Accreditations & Achievements</h2>
          <p className="text-base text-slate-500 mb-12 text-center max-w-lg mx-auto">Officially recognised by India's leading startup and MSME bodies.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: "Kerala Startup Mission (KSUM)", desc: "Recognised as an innovative startup by Kerala Startup Mission, the nodal agency under Govt. of Kerala.", url: CERT_URLS.ksum, logo: "/ksum.svg" },
              { title: "Startup India (DPIIT)", desc: "Certified by the Department for Promotion of Industry and Internal Trade under the Startup India initiative.", url: CERT_URLS.startup_india, logo: "/startup-india.png" },
              { title: "MSME Udyam Registration", desc: "Registered under the Ministry of Micro, Small and Medium Enterprises, Government of India.", url: CERT_URLS.msme, logo: "/msme.svg" },
            ].map((cert, i) => (
              <a key={i} href={cert.url} target="_blank" rel="noopener noreferrer" className="group" data-testid={`cert-card-${i}`}>
                <Card className="bg-white border border-slate-200 rounded-lg shadow-sm hover:shadow-md hover:-translate-y-[1px] transition-all duration-200 h-full">
                  <CardContent className="p-6 flex flex-col items-center text-center">
                    <img src={cert.logo} alt={cert.title} className="h-12 object-contain mb-4 opacity-80 group-hover:opacity-100 transition-opacity" />
                    <h3 className="text-sm font-semibold text-ix-black mb-2">{cert.title}</h3>
                    <p className="text-xs text-slate-500 leading-relaxed mb-3">{cert.desc}</p>
                    <span className="text-xs text-ix-red-600 font-medium flex items-center gap-1">View Certificate <ExternalLink className="w-3 h-3" /></span>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-white" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-ix-black tracking-tight mb-4">Ready to Bridge the Gap?</h2>
          <p className="text-base text-slate-500 mb-10 max-w-lg mx-auto">Join India's first full-stack CSR infrastructure platform.</p>
          <Button className="bg-ix-red-600 text-white hover:bg-ix-red-700 rounded-md px-8 py-3 h-auto text-base shadow-sm" onClick={() => navigate("/auth")} data-testid="cta-join-btn">
            Create Your Account <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
